import { FileText } from 'lucide-react'
import * as React from 'react'
import { getDownloadUrl } from '@/client/sdk.gen'
import type { ArtifactResponse } from '@/client/types.gen'
import { BpmnPreview } from '@/components/bpmn'
import { ArtifactMetadataBlock } from '@/components/project/artifact-viewer/ArtifactMetadataBlock'
import { Spinner } from '@/components/ui/spinner'
import { useTranslation } from '@/hooks/useTranslation'
import { extractErrorMessage } from '@/utils/error'

export interface BpmnViewerProps {
	artifact: ArtifactResponse
	activeTab: 'preview' | 'metadata'
}

export function BpmnViewer({ artifact, activeTab }: BpmnViewerProps) {
	const { t } = useTranslation()
	const [content, setContent] = React.useState<string | null>(null)
	const [loading, setLoading] = React.useState(false)
	const [error, setError] = React.useState<string | null>(null)

	React.useEffect(() => {
		setContent(null)
		setError(null)
		setLoading(false)

		if (artifact.status !== 'completed') return

		let cancelled = false

		const fetchContent = async () => {
			setLoading(true)
			try {
				const response = await getDownloadUrl({
					path: { project_id: artifact.project_id, artifact_id: artifact.id },
				})

				if (cancelled) return

				if (response.error || !response.data?.download_url) {
					setError(
						extractErrorMessage(response.error, t('pages.projectDetail.artifactPreview.errorLoading'))
					)
					return
				}

				const textResponse = await fetch(response.data.download_url)
				if (cancelled) return

				if (!textResponse.ok) {
					setError(t('pages.projectDetail.artifactPreview.errorLoading'))
					return
				}

				const text = await textResponse.text()
				if (cancelled) return

				setContent(text)
			} catch (err) {
				if (cancelled) return
				setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
			} finally {
				if (!cancelled) setLoading(false)
			}
		}

		fetchContent().catch((err) => {
			if (cancelled) return
			setError(extractErrorMessage(err, t('pages.projectDetail.artifactPreview.errorLoading')))
			setLoading(false)
		})

		return () => {
			cancelled = true
		}
	}, [artifact.id, artifact.project_id, artifact.status, t])

	return (
		<div className="flex h-full min-h-0 flex-col">
			{activeTab === 'preview' && (
				<div className="flex min-h-0 flex-1 flex-col overflow-hidden px-4 pb-4">
					<div className="relative min-h-0 flex-1 overflow-hidden rounded-lg border border-border bg-muted/20">
						{artifact.status !== 'completed' ? (
							<div className="flex min-h-full flex-col items-center justify-center p-3 text-center">
								<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
								<p className="mt-3 text-sm font-medium text-foreground">
									{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
								</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{t('pages.projectDetail.artifactPreview.previewPending')}
								</p>
							</div>
						) : loading ? (
							<div className="flex min-h-full items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
								<Spinner className="size-4" aria-hidden />
								{t('common.loading')}
							</div>
						) : error ? (
							<div className="flex min-h-full items-center justify-center p-4">
								<div className="w-full max-w-md rounded-lg border border-[#ff3131]/30 bg-[#ff3131]/10 p-4">
									<p className="text-sm font-semibold text-[#ff3131]">
										{t('pages.projectDetail.artifactPreview.errorLoading')}
									</p>
									<p className="mt-1 text-sm text-red-700 dark:text-red-300">{error}</p>
								</div>
							</div>
						) : content !== null ? (
							<BpmnPreview xml={content} className="min-h-[280px] h-full w-full" />
						) : (
							<div className="flex min-h-full flex-col items-center justify-center p-3 text-center">
								<FileText className="size-12 text-muted-foreground shrink-0" aria-hidden />
								<p className="mt-3 text-sm font-medium text-foreground">
									{t('pages.projectDetail.artifactPreview.previewNotAvailable')}
								</p>
								<p className="mt-1 text-sm text-muted-foreground">
									{t('pages.projectDetail.artifactPreview.previewNotAvailableDescription')}
								</p>
							</div>
						)}
					</div>
				</div>
			)}

			{activeTab === 'metadata' && (
				<div className="space-y-6 p-4">
					<ArtifactMetadataBlock artifact={artifact} />
				</div>
			)}
		</div>
	)
}
